import {
  OrganizationType,
  SITE_OPS_ORG_TYPES,
} from '../types/organization.type';
import type { KnowledgeArticle } from '../types/knowledge-base.type';

const ALL_ORGANIZATION_TYPES = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
] as const;

const SITE_OPS_TYPES = [...SITE_OPS_ORG_TYPES];

export const KNOWLEDGE_BASE_CATEGORY_LABELS: Record<KnowledgeArticle['category'], string> = {
  technical_support: 'Technical Support',
  pickup_orders: 'Pickup & Orders',
  fleet_drivers: 'Fleet & Drivers',
  containers_sites: 'Containers & Sites',
  complaints_suggestions: 'Complaints & Suggestions',
  general_inquiry: 'General',
};

export const KNOWLEDGE_BASE_ARTICLES: KnowledgeArticle[] = [
  // —— Waste Outlet: create / add ——
  {
    id: 'pickup',
    title: 'Pickup',
    summary: 'Create, track, and manage pickup orders.',
    content:
      'Pickup orders schedule waste collection from your sites. Open Pickup Orders in the sidebar to create orders, view the list, and track their status.\n\nSelect a guide below for step-by-step instructions.',
    category: 'pickup_orders',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['pickup', 'orders'],
    children: [
      'view-pickup-orders-list',
      'create-pickup-order-outlet',
      'pickup-order-statuses',
      'filter-pickup-orders-by-date',
    ],
  },
  {
    id: 'view-pickup-orders-list',
    title: 'View Pickup Orders List',
    summary: 'Open the orders table, search, and browse pickup requests.',
    content:
      '1. Open Pickup Orders in the sidebar.\n\n2. The main table lists your organization\'s pickup orders with ID, collection site, status, and dates.\n\n3. Use the search box above the table to find an order by ID or collection site.\n\n4. Scroll the table and use pagination at the bottom to browse more rows.\n\nTip: Status chips and date filters above the table narrow what you see — see the related guides for those tools.',
    category: 'pickup_orders',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['pickup', 'orders', 'list', 'table', 'search'],
  },
  {
    id: 'create-pickup-order-outlet',
    title: 'Create New Pickup Order',
    summary: 'Step-by-step guide to scheduling a waste pickup.',
    content:
      '1. Open Pickup Orders in the sidebar → Create Pickup Order (or click + on the Pickup Orders page).\n\n2. Pickup order details:\n   • Material type (required) — waste category and unit are set automatically.\n\n3. Location & container:\n   • Collection site (required)\n   • Container at that site\n   • Fill level — Full, Three quarter full, Half full, or Quarter full\n\n4. Click Save. The order appears on the list with status Pending.',
    category: 'pickup_orders',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['pickup', 'orders', 'create'],
  },
  {
    id: 'material',
    title: 'Material',
    summary: 'Add, edit, and manage material types for pickup orders.',
    content:
      'Use the guides below to manage material types. All material actions are under Recycling → Material Types in the sidebar.',
    category: 'general_inquiry',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['recycling', 'material'],
    children: ['add-material-type', 'edit-material-type'],
  },
  {
    id: 'add-material-type',
    title: 'Add Material Type',
    summary: 'Register a waste material type for pickup orders.',
    content:
      '1. Go to Recycling in the sidebar → Material Types.\n\n2. Click Add Material (top right).\n\n3. Enter the material name, category, and unit of measure (e.g. kg, tons, bags).\n\n4. Save. The material becomes available when creating pickup orders.\n\nTip: Material type determines the waste category and measurement unit on pickup order forms.',
    category: 'general_inquiry',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['recycling', 'material', 'create'],
  },
  {
    id: 'container',
    title: 'Container',
    summary: 'Add, manage, and track waste containers.',
    content:
      'Use the guides below to manage containers. All container actions are under Containers → Container List in the sidebar.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['containers'],
    children: [
      'add-container',
      'container-fill-level',
      'container-statuses',
      'print-container-qr',
      'edit-or-delete-container',
    ],
  },
  {
    id: 'add-container',
    title: 'Add New Container',
    summary: 'Register a waste container at a collection site.',
    content:
      '1. Go to Containers in the sidebar → Container List.\n\n2. Click Add Container (top right).\n\n3. Fill in container details: name, material type, collection site, status, and other required fields.\n\n4. Save. The container appears in the list and can be selected when creating pickup orders.\n\nTip: Print the container QR code after creation for field identification (see Print Container QR Code guide).',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['containers', 'create'],
  },
  {
    id: 'collection-site',
    title: 'Collection Site',
    summary: 'Add, edit, and manage waste collection locations.',
    content:
      'Use the guides below to manage collection sites. All site actions are under Containers → Collection Sites in the sidebar.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['collection', 'sites'],
    children: ['add-collection-site', 'edit-or-delete-collection-site'],
  },
  {
    id: 'add-collection-site',
    title: 'Add New Collection Site',
    summary: 'Create a location where waste is collected.',
    content:
      '1. Go to Containers in the sidebar → Collection Sites.\n\n2. Click Add Site (top right).\n\n3. Enter the site name, address, and any required location details.\n\n4. Save. The site can now be linked to containers and selected on pickup order forms.\n\nTip: You need at least one collection site before creating pickup orders.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['collection', 'sites', 'create'],
  },

  // —— Waste Mover & Recycler: create / add ——
  {
    id: 'truck',
    title: 'Truck',
    summary: 'Add vehicles, understand fleet statuses, and manage your trucks.',
    content:
      'Use the guides below to manage your fleet. All truck actions are under Trucks → Fleet Management in the sidebar.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['trucks', 'fleet'],
    children: ['add-vehicle', 'truck-statuses', 'edit-or-delete-vehicle'],
  },
  {
    id: 'add-vehicle',
    title: 'Add Vehicle',
    summary: 'Register a new truck or fleet vehicle with full details.',
    content:
      '1. Go to Trucks in the sidebar to open Fleet Management.\n\n2. Click Add Vehicle (top right).\n\n3. Fill in the form:\n   • Plate numbers (Arabic and English)\n   • Vehicle Name (required)\n   • Capacity and Capacity Unit (kg, ton, or lb)\n   • Vehicle Type (Truck, Van, Car, Bus, or Trailer)\n   • Car Model, VIN, Empty Weight, and Fuel Consumption (optional but recommended)\n   • Status — set Available if the vehicle is ready for assignment\n\n4. Click Save. The vehicle appears in the fleet table with name, capacity, status, plate, and VIN.\n\nTip: Set status to Available only when the vehicle is ready to be assigned to routes or drivers.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['trucks', 'fleet', 'vehicle', 'create'],
  },
  {
    id: 'driver',
    title: 'Driver',
    summary: 'Register drivers, view profiles, and manage truck assignments.',
    content:
      'Use the guides below to manage drivers. All driver actions are under Drivers in the sidebar.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['drivers', 'fleet'],
    children: ['add-driver', 'view-driver-details', 'edit-or-delete-driver'],
  },
  {
    id: 'add-driver',
    title: 'Add Driver',
    summary: 'Create a driver account with license and personal details.',
    content:
      '1. Go to Drivers in the sidebar.\n\n2. Click Add Driver (top right).\n\n3. Account section:\n   • Username (Email) — required login email\n   • Password and Confirm Password — required\n\n4. Personal section:\n   • First name, Last name, Phone (Saudi format: 05XXXXXXXX or +9665XXXXXXXX)\n   • Gender, Nationality, Date of birth, IBAN (optional)\n\n5. Driver & vehicle section:\n   • Driver license number (required)\n   • Driver license expiry\n   • Vehicle registration number\n\n6. Click Save. The driver appears in the Drivers table.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['drivers', 'create'],
  },

  // —— All organization types ——
  {
    id: 'understanding-notifications',
    title: 'Understanding Notifications',
    summary: 'How to enable, read, and manage platform alerts.',
    content:
      'Enable browser notifications:\nWhen the browser asks for permission, click Allow so you can receive order and platform updates outside the app.\n\nView notifications:\n1. Click the bell icon in the top bar.\n2. The panel shows unread notifications with title, message, and time.\n3. Click a notification to mark it as read.\n4. Use Mark all as read to clear all unread items at once.\n\nImportant:\n• The red badge on the bell shows how many unread notifications you have.',
    category: 'technical_support',
    organizationTypes: [...ALL_ORGANIZATION_TYPES],
    tags: ['notifications', 'alerts', 'bell'],
  },

  // —— Waste Outlet: operations & reference ——
  {
    id: 'pickup-order-statuses',
    title: 'Pickup Order Statuses',
    summary: 'What each order status means as it moves through operations.',
    content:
      'View the current status on the Pickup Orders list. Status updates automatically as the order progresses.\n\n• Pending — order was created and is awaiting processing or assignment.\n• In transit — pickup or delivery is underway.\n• Completed — the order was fulfilled successfully.\n• Cancelled — the order was cancelled and will not be completed.',
    category: 'pickup_orders',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['pickup', 'status', 'pending', 'completed'],
  },
  {
    id: 'filter-pickup-orders-by-date',
    title: 'Filter Pickup Orders by Date',
    summary: 'Find orders within a specific date range.',
    content:
      '1. Open Pickup Orders in the sidebar.\n\n2. Locate the Filter by date section.\n\n3. Set From and To dates for the range you need.\n\n4. Click Apply dates to filter the list.\n\n5. Click Clear dates to remove the filter and return to the default view.',
    category: 'pickup_orders',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['pickup', 'filter', 'date', 'search'],
  },
  {
    id: 'container-fill-level',
    title: 'Container Fill Level',
    summary: 'How to choose the right fill level for a container.',
    content:
      'When you select a container on the Create Pickup Order form, fill level becomes required.\n\nOptions:\n• Full — container is completely full\n• Three quarter full — approximately 75% full\n• Half full — approximately 50% full\n• Quarter full — approximately 25% full\n\nChoose the level that best reflects the actual container state. Accurate fill levels help drivers and operations plan collection capacity and routes.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['container', 'fill', 'level'],
  },
  {
    id: 'container-statuses',
    title: 'Container Statuses',
    summary: 'What each container status means and when to use it.',
    content:
      'Set or review status when adding or editing a container on Container List.\n\n• Ready for pickup — container is full or ready and available for collection.\n• Awaiting pickup — pickup has been scheduled or is waiting.\n• Empty — no waste to collect.\n• Maintenance — container is temporarily out of service.\n• Pending — container is being set up.\n• Inactive — container is not in use.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['container', 'status'],
  },
  {
    id: 'print-container-qr',
    title: 'Print Container QR Code',
    summary: 'Generate and print a QR label for field use.',
    content:
      '1. Go to Containers → Container List.\n\n2. Find the container in the table.\n\n3. Click the QR icon on its row.\n\n4. Use the browser print dialog to print the QR label.\n\nAttach the label to the physical container for easy identification and scanning in the field.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['container', 'qr', 'print', 'label'],
  },
  {
    id: 'edit-material-type',
    title: 'Edit or Delete Material Type',
    summary: 'Update or remove a material type from recycling settings.',
    content:
      '1. Go to Recycling → Material Types.\n\n2. To edit: click the edit icon on the row → update name, category, or unit of measure → save.\n\n3. To delete: click the delete icon on the row → confirm.\n\nWarning: Do not delete a material type that is linked to active pickup orders. Update or complete those orders first.',
    category: 'general_inquiry',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['recycling', 'material', 'edit', 'delete'],
  },
  {
    id: 'edit-or-delete-container',
    title: 'Edit or Delete Container',
    summary: 'Update or remove a container from the list.',
    content:
      '1. Go to Containers → Container List.\n\n2. To edit: click the edit icon on the container row → update details → save.\n\n3. To delete: click the delete icon on the container row → confirm.\n\nWarning: Containers may be linked to pickup orders. Confirm carefully before deleting — active orders may be affected.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['container', 'edit', 'delete'],
  },
  {
    id: 'edit-or-delete-collection-site',
    title: 'Edit or Delete Collection Site',
    summary: 'Update or remove a collection site.',
    content:
      '1. Go to Containers → Collection Sites.\n\n2. Click edit or delete on the site card.\n\n3. Confirm any deletion prompt.\n\nWarning: Collection sites may be linked to containers and pickup orders. Confirm carefully before deleting.',
    category: 'containers_sites',
    organizationTypes: SITE_OPS_TYPES,
    tags: ['site', 'edit', 'delete'],
  },

  // —— Waste Mover & Recycler: operations & reference ——
  {
    id: 'truck-statuses',
    title: 'Truck Statuses',
    summary: 'What each fleet vehicle status means and when to use it.',
    content:
      'Each vehicle has a status shown as a chip in the Fleet Management table. Update status when adding or editing a vehicle.\n\nStatus meanings:\n• Available — vehicle is ready for assignment to a driver or route.\n• Unavailable — vehicle cannot be used (not in service, out of rotation, etc.).\n• In transit — vehicle is on the road between locations.\n• Loading / Unloading — vehicle is at a stop loading or unloading waste.\n• Maintenance — vehicle is under repair or scheduled service.\n\nBest practice:\n• Set Available only when the truck is operational.\n• Switch to Maintenance during repairs so operations teams do not assign trips to it.\n• Use In transit or Loading / Unloading to reflect real-time operations.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['truck', 'fleet', 'status'],
  },
  {
    id: 'view-driver-details',
    title: 'View Driver Details',
    summary: 'Open a driver profile, license info, and assigned truck.',
    content:
      '1. Go to Drivers in the sidebar.\n\n2. Find the driver in the table. Columns show: Username (Email), Driver License, Vehicle Registration, and Assigned Truck.\n\n3. Click the view (eye) icon on the row to open Driver details.\n\n4. The dialog shows:\n   • Account — email / username\n   • Personal — name, phone, gender, nationality, date of birth, IBAN\n   • Driver & vehicle — license number, license expiry, vehicle registration, assigned truck\n\n5. Click Edit in the dialog, or close and use the row menu for more actions.\n\nIf Assigned Truck shows "Not assigned", use the row menu → Assign to Truck to link the driver to an available vehicle.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['driver', 'details', 'view'],
  },
  {
    id: 'edit-or-delete-vehicle',
    title: 'Edit or Delete Vehicle',
    summary: 'Update vehicle details or remove a truck from the fleet.',
    content:
      'Edit a vehicle:\n1. Go to Trucks → Fleet Management.\n2. Click the edit (pencil) icon on the vehicle row, OR click the view (eye) icon and choose Edit in the details modal.\n3. Update fields (name, capacity, plate, status, VIN, etc.) and save.\n\nDelete a vehicle:\n1. Click the delete (trash) icon on the vehicle row, OR open details and choose Delete.\n2. Confirm the deletion in the prompt.\n\nWarning: Deleting a vehicle may affect active operations if it is assigned to a driver or linked to ongoing trips. Consider setting status to Unavailable or Maintenance instead of deleting when temporarily out of service.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['truck', 'edit', 'delete', 'fleet'],
  },
  {
    id: 'edit-or-delete-driver',
    title: 'Edit Driver & Assign Truck',
    summary: 'Update driver information and manage truck assignments.',
    content:
      'Edit driver details:\n1. Go to Drivers in the sidebar.\n2. Open the row menu → Edit Driver.\n3. Update account, personal, or license fields and save.\n\nAlternatively: click the view (eye) icon → Edit in the details dialog.\n\nAssign to a truck:\n1. Open the row menu → Assign to Truck.\n2. Select an available truck from the list (only trucks without a driver appear).\n3. Confirm. The Assigned Truck column updates with the truck name.\n\nUnassign from truck:\n1. Open the row menu → Unassign from Truck (visible only when a truck is assigned).\n2. Confirm. The driver is no longer linked to that vehicle.',
    category: 'fleet_drivers',
    organizationTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
    tags: ['driver', 'edit', 'assign', 'truck'],
  },
];

const CHILD_ARTICLE_IDS = new Set(
  KNOWLEDGE_BASE_ARTICLES.flatMap((article) => article.children ?? [])
);

export const getKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  KNOWLEDGE_BASE_ARTICLES.filter((article) => article.organizationTypes.includes(organizationType));

export const getTopLevelKnowledgeArticlesForOrganizationType = (
  organizationType: OrganizationType
): KnowledgeArticle[] =>
  getKnowledgeArticlesForOrganizationType(organizationType).filter((article) => !CHILD_ARTICLE_IDS.has(article.id));

export const getKnowledgeArticleById = (id: string): KnowledgeArticle | undefined =>
  KNOWLEDGE_BASE_ARTICLES.find((article) => article.id === id);

export const getKnowledgeArticleChildren = (
  article: KnowledgeArticle,
  organizationType: OrganizationType
): KnowledgeArticle[] => {
  if (!article.children?.length) return [];
  const allowed = new Set(
    getKnowledgeArticlesForOrganizationType(organizationType).map((item) => item.id)
  );
  return article.children
    .map((childId) => getKnowledgeArticleById(childId))
    .filter((child): child is KnowledgeArticle => Boolean(child && allowed.has(child.id)));
};

export const getKnowledgeArticleParentId = (articleId: string): string | undefined => {
  const parent = KNOWLEDGE_BASE_ARTICLES.find((article) => article.children?.includes(articleId));
  return parent?.id;
};
